import React, { useCallback, useMemo, useRef, useState } from "react";
import Papa from "papaparse";
import "./CsvUploader.css";

export default function CsvUploader({
  requiredHeaders,
  validateOrder = false,
  sampleRows = 3,
  onData,
  postUrl,
  requestHeaders,
  delimiter,
  className = "",
}) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [errors, setErrors] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [posted, setPosted] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);

  const hasErrors = errors.length > 0;

  const onPick = () => inputRef.current?.click();

  const reset = () => {
    setFile(null);
    setRows([]);
    setHeaders([]);
    setErrors([]);
    setPosted(null);
    setDragOver(false);
    setSearchText("");
    setFilteredData([]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const validateHeaders = useCallback(
    (found) => {
      if (!requiredHeaders || requiredHeaders.length === 0) return [];
      const errs = [];
      const foundSet = new Set(found);
      requiredHeaders.forEach((h) => {
        if (!foundSet.has(h)) errs.push(`Missing required header: "${h}"`);
      });
      found.forEach((h) => {
        if (!requiredHeaders.includes(h)) {
          errs.push(`Unexpected header present: "${h}"`);
        }
      });
      if (validateOrder) {
        for (let i = 0; i < requiredHeaders.length; i++) {
          if (found[i] !== requiredHeaders[i]) {
            errs.push(
              `Header order mismatch at position ${i + 1}. Expected "${requiredHeaders[i]}" but found "${found[i] ?? "<none>"}".`
            );
          }
        }
      }
      return errs;
    },
    [requiredHeaders, validateOrder]
  );

  const parseFile = useCallback(
    (f) => {
      setParsing(true);
      setErrors([]);
      Papa.parse(f, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: true,
        delimiter: delimiter || undefined,
        complete: (res) => {
          const data = res.data || [];
          const metaHeaders = res.meta?.fields || [];
          const valErrs = validateHeaders(metaHeaders);
          setHeaders(metaHeaders);
          setRows(data);
          setErrors(valErrs);
          setParsing(false);
          setPosted(null);
          if (!valErrs.length && typeof onData === "function") onData(data);
        },
        error: (err) => {
          setParsing(false);
          setErrors([`Parse error: ${err?.message || String(err)}`]);
        },
      });
    },
    [delimiter, onData, validateHeaders]
  );

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f && f.name.toLowerCase().endsWith(".csv")) {
      setFile(f);
      parseFile(f);
    }
  };

  const onDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };
  const onBrowse = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      parseFile(f);
    }
  };

  // Search function for multiple SKU IDs
  const searchSKU = () => {
    if (!searchText) {
      setFilteredData([]);
      return;
    }
    const skuIds = searchText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    const result = rows
      .filter((r) => skuIds.includes(String(r.skuID)))
      .map((r) => ({ skuID: r.skuID, ProductIds: r.ProductId }));
    setFilteredData(result);
  };

  // Copy matched Product IDs to clipboard
  const copyProductIDs = () => {
    if (!filteredData.length) return;
    const allIds = filteredData.map((item) => item.ProductIds).join(", ");
    navigator.clipboard.writeText(allIds)
      .then(() => alert("Product IDs copied!"))
      .catch(() => alert("Failed to copy"));
  };

  return (
    <div className={`csv-uploader ${className}`}>
      <div className="header">
        <h2>CSV Upload</h2>
        <div>
          {requiredHeaders?.length && <button onClick={() => {
            const sample = [requiredHeaders.join(",")];
            for (let i = 1; i <= sampleRows; i++) {
              sample.push(requiredHeaders.map((h) => `${h}_${i}`).join(","));
            }
            const blob = new Blob([sample.join("\n") + "\n"], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "sample.csv";
            a.click();
            URL.revokeObjectURL(url);
          }}>Download sample</button>}
          <button onClick={reset}>Reset</button>
        </div>
      </div>

      <div
        className={`csv-dropzone ${dragOver ? "dragover" : ""}`}
        onDragEnter={onDragEnter}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={onPick}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: "none" }}
          onChange={onBrowse}
        />
        <p>Drag & drop a .csv file here, or click to browse</p>
        {file && <p>Selected: {file.name}</p>}
      </div>

      {hasErrors && (
        <div className="csv-errors">
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>Validation issues found:</span>
            <button onClick={() => {
              const blob = new Blob([errors.join("\n")], { type: "text/plain;charset=utf-8" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = "csv-upload-errors.txt";
              a.click();
              URL.revokeObjectURL(url);
            }}>Download details</button>
          </div>
          <ul>{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      )}

      {rows.length > 0 && (
        <div className="csv-search">
          <h3>Search SKU IDs</h3>
          <textarea
            rows={5}
            placeholder="Enter SKU IDs, one per line"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: "100%", marginBottom: "0.5rem" }}
          />
          <button onClick={searchSKU}>Search</button>
        </div>
      )}

      {filteredData.length > 0 && (
        <div className="csv-preview">
          <button onClick={copyProductIDs} style={{ marginBottom: "0.5rem" }}>
            Copy Product IDs
          </button>
          <table>
            <thead>
              <tr>
                <th>SKU ID</th>
                <th>Product IDs</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.skuID}</td>
                  <td>{item.ProductIds}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
